import { Provider, useDispatch, useSelector } from 'react-redux';
import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import axios from 'axios';
import { ConfigProvider } from 'antd';
import { StyleProvider } from '@ant-design/cssinjs';

import './utils/dayjs-setup';
import { API_URL, FP_API_URL, TENANT_ID } from './constants';
import EventCalendarContextProvider from './containers/event-calendar/context';
import { I18NContextProvider } from './i18n';
import ModalContextProvider from './components/home/ModalProvider';
import createStore from './state/store/createStore';
import { RootState } from './state/reducers';
import { getToken } from './state/selectors/auth';
import { getPrivileges } from './state/selectors/privilege';
import { fetchPrivilegesAction } from './state/actions/privileges';
import { fetchTenantConfigAction } from './state/actions/tenant';

import { antdCache } from '../gatsby-ssr';

// Global query configuration, can be overwritten by each query option
// const queryConfig = {
//   refetchAllOnWindowFocus: false, // disable default window focus re-fetch
//   staleTime: Infinity, // data will never stale
//   cacheTime: 0, // no cache
// };

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const store = createStore();

// define global axios interceptors to set `Authorization` header and current tenant
axios.interceptors.request.use(function (config) {
  const { url = '' } = config;
  // only add `Authorization` header for prosper request
  const shouldIncludeAuthorization = url.search(API_URL) > -1 || (FP_API_URL && url.search(FP_API_URL) > -1);

  // only add `tenant` header for prosper request
  const shouldIncludeTenant = url.search(API_URL) > -1 || (FP_API_URL && url.search(FP_API_URL) > -1);
  const token = getToken(store.getState());

  if (shouldIncludeAuthorization && token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (shouldIncludeTenant) {
    config.headers.tenant = TENANT_ID;
  }

  return config;
});

/* When the app boots with a token already in localStorage (e.g. the user logged in
  via the webfront, which stores the token and hands off here), the login-page useEffect
  that used to fetch privileges and tenant config never runs. Without privileges the
  admin header and permission-gated menus don't render. */
function SessionBootstrap({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch();
  const token = useSelector(getToken);
  const privilegesLoaded = useSelector((state: RootState) => getPrivileges(state)?.permissions !== undefined);

  useEffect(() => {
    if (token && !privilegesLoaded) {
      dispatch(fetchPrivilegesAction() as any);
    }
  }, [dispatch, token, privilegesLoaded]);

  useEffect(() => {
    if (token) {
      dispatch(fetchTenantConfigAction() as any);
    }
  }, [dispatch, token]);

  return <>{children}</>;
}

// root component for all the gatsby pages
export default function RootWrapper({ element }: { element: React.ReactNode }) {
  return (
    <StyleProvider cache={antdCache}>
      <ConfigProvider theme={{ hashed: false }}>
        <QueryClientProvider client={queryClient}>
          <Provider store={store}>
            <SessionBootstrap>
              <I18NContextProvider>
                <EventCalendarContextProvider>
                  <ModalContextProvider>{element}</ModalContextProvider>
                </EventCalendarContextProvider>
              </I18NContextProvider>
            </SessionBootstrap>
          </Provider>
        </QueryClientProvider>
      </ConfigProvider>
    </StyleProvider>
  );
}
